import type { CSSProperties } from 'react';
import { CalendarDays } from 'lucide-react';
import { Tone } from '@gokrew/models/ui.types';
import { LeaveDuration } from '@gokrew/models/leaves';
import { cn, getColorForText } from '@/lib/utils';
import { AVATAR_COLORS } from '@/constants/avatar-colors';
import { getUserInitials } from '@/utility/utility';
import {
  buildPresenceBuckets,
  type OnLeaveDurationKind,
} from '@/utility/onLeavePresence';
import { Chip } from '../Chip';
import { TruncatedTooltip } from '../TruncatedTooltip';
import styles from './OnLeave.module.css';

const TAG_TONE: Record<OnLeaveDurationKind, Tone> = {
  [LeaveDuration.FULL_DAY]: Tone.ERROR,
  [LeaveDuration.HALF_DAY]: Tone.WARNING,
  [LeaveDuration.HOURLY]: Tone.SOFT_BLUE,
};

export function OnLeavePresenceBar({
  presence,
}: {
  presence: ReturnType<typeof buildPresenceBuckets>;
}) {
  return (
    <div className={styles.presence}>
      <div className={styles.bar}>
        {presence.segments.map((segment) => (
          <span
            key={segment.key}
            className={styles.barSegment}
            style={{ width: segment.width, background: segment.color }}
          />
        ))}
      </div>
      <div className={styles.legend}>
        {presence.buckets.map((bucket) => (
          <span key={bucket.key} className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{ background: bucket.color }}
            />
            {bucket.label} <b className={styles.legendCount}>{bucket.count}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

/** "Next working day: Tuesday, October 13", the line a non-working day
 *  shows above that day's absences. */
export function OnLeaveNextWorkingDay({ label }: { label: string }) {
  return (
    <p className={styles.nextDay}>
      <CalendarDays size={14} className={styles.nextDayIcon} />
      {label}
    </p>
  );
}

export interface OnLeaveDay {
  key: string;
  label: string;
  count: number;
}

export function OnLeaveDaySelector({
  label,
  days,
  activeKey,
  todayKey,
  onSelect,
  compact = false,
}: {
  label: string;
  days: OnLeaveDay[];
  activeKey: string;
  todayKey: string;
  onSelect: (key: string) => void;
  /** Tighter strip, day and count on one line (company dashboard). */
  compact?: boolean;
}) {
  return (
    <div className={cn(styles.selector, compact && styles.selectorCompact)}>
      <p className={styles.selectorLabel}>{label}</p>
      <div
        className={styles.days}
        style={{ '--day-count': days.length } as CSSProperties}
      >
        {days.map((day) => (
          <button
            key={day.key}
            type="button"
            aria-pressed={day.key === activeKey}
            aria-current={day.key === todayKey ? 'date' : undefined}
            className={cn(
              styles.day,
              day.key === todayKey && styles.dayToday,
              day.key === activeKey && styles.dayActive
            )}
            onClick={() => onSelect(day.key)}
          >
            <span className={styles.dayLabel}>{day.label}</span>
            <span className={styles.dayCount}>{day.count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function OnLeaveRow({
  employee,
  meta,
  kind,
  tag,
}: {
  employee: { firstName: string; lastName: string };
  meta: string;
  kind: OnLeaveDurationKind;
  tag: string;
}) {
  const name = `${employee.firstName} ${employee.lastName}`.trim();

  return (
    <div className={styles.row}>
      <div
        className={styles.avatar}
        style={{ background: getColorForText(name, AVATAR_COLORS) }}
      >
        {getUserInitials(employee)}
      </div>
      <div className={styles.body}>
        <TruncatedTooltip lines={1} className={styles.name} text={name} />
        <TruncatedTooltip lines={1} className={styles.meta} text={meta} />
      </div>
      <Chip size="sm" type={TAG_TONE[kind]} className={styles.tag}>
        {tag}
      </Chip>
    </div>
  );
}
